import {Column, FormControl, Table, TableRow, TableRowAction} from 'platform/components';
import {Box, Space, Text} from 'platform/foundation';

import {UseFormReturn} from 'react-hook-form';
import {useLocation} from 'react-router-dom';

import {GetServiceOrderVariantDefaultWarehousePriorityApiResponse} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {SettingsFooter} from '@dms/settings/shared';

import {useNavigate} from 'shared/web';

import {FormType} from '../../types/ServiceOrderVariant';
import {getServiceOrderVariantsRoutes} from '../../utils/getServiceOrderVariantsRoutes';

const TABLE_COLUMNS: Column[] = [
  {
    element: (
      <Box padding={4} backgroundColor="palettes.neutral.10.100">
        <Text alternative size="small" align="left">
          {i18n.t('page.serviceOrderVariantsDetail.warehousePriorities.priority')}
        </Text>
      </Box>
    ),
    width: '10%',
  },
  {
    element: (
      <Box padding={4} backgroundColor="palettes.neutral.10.100">
        <Text alternative size="small" align="left">
          {i18n.t('page.serviceOrderVariantsDetail.warehousePriorities.warehouseName')}
        </Text>
      </Box>
    ),
    width: '25%',
  },
  {
    element: (
      <Box padding={4} backgroundColor="palettes.neutral.10.100">
        <Text alternative size="small" align="left">
          {i18n.t('page.serviceOrderVariantsDetail.warehousePriorities.warehouseCode')}
        </Text>
      </Box>
    ),
    width: '20%',
  },
  {
    element: (
      <Box padding={4} backgroundColor="palettes.neutral.10.100">
        <Text alternative size="small" align="left">
          {i18n.t('page.serviceOrderVariantsDetail.warehousePriorities.profile')}
        </Text>
      </Box>
    ),
    width: '25%',
  },
  {
    element: (
      <Box padding={4} backgroundColor="palettes.neutral.10.100">
        <Text alternative size="small" align="left">
          {i18n.t('page.serviceOrderVariantsDetail.warehousePriorities.branch')}
        </Text>
      </Box>
    ),
    width: '20%',
  },
  {
    element: <Box flex={1} height={13} backgroundColor="palettes.neutral.10.100" />,
  },
];

export interface WarehousePrioritiesTabProps {
  control: FormControl<FormType>;
  formApi: UseFormReturn<FormType>;
  sourceData: GetServiceOrderVariantDefaultWarehousePriorityApiResponse | undefined;
}

export function WarehousePrioritiesTab(props: WarehousePrioritiesTabProps) {
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const serviceOrderVariantsRoutes = getServiceOrderVariantsRoutes(pathname);

  const formWarehousePriorities = props.formApi.watch('warehousePriorities');

  const displayWarehousePriorities =
    formWarehousePriorities && props.sourceData
      ? (formWarehousePriorities
          .map((warehouseId) =>
            props.sourceData?.find((warehouse) => warehouse.warehouseId === warehouseId)
          )
          .filter(Boolean) as typeof props.sourceData)
      : props.sourceData || [];

  const handleMoveUp = (currentIndex: number) => {
    if (!displayWarehousePriorities || currentIndex === 0) {
      return;
    }

    const newOrder = [...displayWarehousePriorities];
    const item = newOrder.splice(currentIndex, 1)[0];
    newOrder.splice(currentIndex - 1, 0, item);

    const warehouseIds = newOrder.map((warehouse) => warehouse.warehouseId);

    props.formApi.setValue('warehousePriorities', warehouseIds);
  };

  const handleMoveDown = (currentIndex: number) => {
    if (!displayWarehousePriorities || currentIndex === displayWarehousePriorities.length - 1) {
      return;
    }

    const newOrder = [...displayWarehousePriorities];
    const item = newOrder.splice(currentIndex, 1)[0];
    newOrder.splice(currentIndex + 1, 0, item);

    const warehouseIds = newOrder.map((warehouse) => warehouse.warehouseId);

    props.formApi.setValue('warehousePriorities', warehouseIds);
  };

  return (
    <>
      <Table columns={TABLE_COLUMNS} variant="bordered">
        {displayWarehousePriorities?.map((warehouse, index) => {
          const canMoveUp = index > 0;
          const canMoveDown = index < displayWarehousePriorities.length - 1;

          const actions: {primary: TableRowAction[]} = {
            primary: [
              {
                title: i18n.t('page.serviceOrderVariantsDetail.warehousePriorities.moveUp'),
                icon: 'navigation/arrow_upward',
                isDisabled: !canMoveUp,
                onClick: () => handleMoveUp(index),
              },
              {
                title: i18n.t('page.serviceOrderVariantsDetail.warehousePriorities.moveDown'),
                icon: 'navigation/arrow_downward',
                isDisabled: !canMoveDown,
                onClick: () => handleMoveDown(index),
              },
            ],
          };

          return (
            <TableRow key={warehouse.warehouseId} actions={actions}>
              <Box paddingHorizontal={4}>
                <Text size="small">{warehouse.warehousePriority}</Text>
              </Box>
              <Box paddingHorizontal={4}>
                <Text size="small">{warehouse.warehouseName}</Text>
              </Box>
              <Box paddingHorizontal={4}>
                <Text size="small">{warehouse.warehouseNumber}</Text>
              </Box>
              <Box paddingHorizontal={4}>
                <Text size="small">{warehouse.authorizationProfile?.name}</Text>
              </Box>
              <Box paddingHorizontal={4}>
                <Text size="small">{warehouse.branch?.name}</Text>
              </Box>
            </TableRow>
          );
        })}
      </Table>
      <Space vertical={20} />
      <SettingsFooter
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            title: i18n.t('general.actions.discardChanges'),
            onClick: () => navigate(serviceOrderVariantsRoutes.overview),
          },
          {
            type: 'form-button',
            control: props.control,
            buttonType: 'submit',
            variant: 'primary',
            title: i18n.t('general.actions.saveChanges'),
          },
        ]}
      />
    </>
  );
}
